import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { Callout } from "@/components/guides/Callout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { TryTool } from "@/components/guides/TryTool";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["check-digits-explained"];

const TOC = [
  { id: "the-short-answer", label: "The short answer" },
  { id: "mod-10", label: "GS1 mod-10: the math walkthrough" },
  { id: "why-it-catches", label: "Why mod-10 catches typos and transpositions" },
  { id: "isbn-10", label: "ISBN-10's mod-11 variant" },
  { id: "code-128", label: "Code 128's modulo-103 checksum" },
  { id: "common-mistakes", label: "Common mistakes" },
  { id: "what-is-a-check-digit", label: "What Is a Check Digit?" },
  { id: "luhn-vs-gs1", label: "Luhn vs GS1 Mod 10" },
  { id: "check-digit-examples", label: "Check Digit Examples" },
  { id: "references", label: "References" },
];

const FAQ = [
  {
    q: "What's a check digit, in plain English?",
    a: "It's an extra digit at the end of an identifier — UPC, EAN, GTIN, ISBN, SSCC — that's computed from the other digits using a fixed formula. If the rest of the number gets typed wrong, the computed value won't match the printed one, and the system flags the input as invalid before it does any damage.",
  },
  {
    q: "Is the check digit the same algorithm for UPC, EAN, GTIN and ISBN-13?",
    a: "Yes for the four most common cases. UPC-A, EAN-13, GTIN-8/12/13/14, ISBN-13 and SSCC all use the GS1 mod-10 algorithm. ISBN-10 is the odd one out — it uses a separate mod-11 algorithm with X as a possible check character. Code 128 has its own internal modulo-103 checksum that's invisible in the decoded string.",
  },
  {
    q: "What Kinds of Typos Does Mod-10 Catch?",
    a: "Every single-digit error (one digit typed wrong) is guaranteed to be caught. Most adjacent-digit transpositions (swapping two neighbouring digits) are caught too — except a handful of specific pairs like 0↔5 where the weighting cancels out. Together those are the two most common mistakes people make when re-keying a number, and catching them costs one extra digit.",
  },
  {
    q: "Why does ISBN-10 use a different algorithm?",
    a: "ISBN-10 predates GS1 mod-10 by a few years and was designed around a mod-11 check (which catches every single-digit error and every transposition of two digits). When ISBN-13 was introduced in 2007, the publishing industry standardised on the GS1 mod-10 already used everywhere else, so any new ISBN minted today uses the EAN-13 algorithm.",
  },
  {
    q: "Where Does the X in Some ISBN-10s Come From?",
    a: "It's the check character for the number 10 — mod-11 can produce a remainder of 10, which doesn't fit in a single decimal digit. The convention is to write X (the Roman numeral) instead. You'll only see X at the very end of an ISBN-10, never anywhere else.",
  },
  {
    q: "Does Code 128's check digit appear in the decoded string?",
    a: "No. Code 128's modulo-103 checksum is part of the symbol's internal structure, not the encoded payload. The scanner verifies it during decoding and either returns the plain payload or an error — your software sees clean text either way. Only the symbol designer needs to think about modulo-103.",
  },
  {
    q: "Can a Check Digit Be Zero?",
    a: "Yes. In GS1 mod 10 a check digit of 0 appears whenever the weighted sum of the body is already a multiple of 10, as with the GTIN-13 5012345678900 used throughout this guide. A 0 is just as valid as any other check digit and must be printed and stored like one.",
  },
  {
    q: "What Is the Difference Between a Check Digit and a Checksum?",
    a: "A check digit is a single character added to a number that people read, type and print, such as the last digit of a GTIN. Checksum is the broader term for any value computed from data to detect errors, and it can be longer or hidden, like the modulo-103 check character inside a Code 128 symbol that never appears in the decoded text. Every check digit is a kind of checksum, but not every checksum is a check digit.",
  },
];

export const Route = createFileRoute("/barcode-guides/check-digits-explained")({
  component: CheckDigitsGuide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function CheckDigitsGuide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/what-is-a-gtin",
          label: "What is a GTIN?",
          desc: "The identifier the check digit protects, in four lengths.",
        },
        {
          to: "/barcode-guides/code-128-explained",
          label: "Code 128 explained",
          desc: "The symbology where the check digit is invisible — modulo-103 inside the symbol.",
        },
        {
          to: "/check-digit-calculator",
          label: "Check digit calculator",
          desc: "Compute the GS1 mod-10 check digit for a GTIN-8, 12, 13 or 14 body.",
        },
        {
          to: "/gtin-validator",
          label: "GTIN validator",
          desc: "Confirm that a complete GTIN carries the right check digit.",
        },
      ]}
    >
      <p>
        A supplier sends a spreadsheet of GTINs for a product onboarding. Five hundred rows. Someone
        copies it into the master data and the import fails on row 247: GTIN reported as invalid.
        The two rows on either side validate fine. The trailing digit on row 247 is a <code>1</code>
        ; the supplier insists it should be a <code>0</code>. Pull up the check-digit calculator and
        the answer falls out in a second — the rest of the row computes a check digit of <code>0</code>, the printed <code>1</code> is the typo, the supplier needs to fix their feed
        before re-sending.
      </p>
      <p>
        That whole exchange exists because of <strong>check digits</strong>. Every UPC, EAN, GTIN,
        ISBN, ISSN and SSCC ends in a single trailing digit computed from the rest by a fixed
        algorithm. The math behind it is small enough to do on paper, the cost is one digit of
        address space, and the payoff is catching every single-digit typo, and most swapped digits, that would otherwise propagate
        quietly through pipelines. This guide unpacks the GS1 mod-10 algorithm in plain English,
        walks through the ISBN-10 mod-11 variant for completeness, and explains where Code
        128&apos;s invisible modulo-103 checksum fits.
      </p>

      <GuideArticleSection id="the-short-answer" title="The Short Answer">
        <p>
          A check digit is the last digit of an identifier, computed from the preceding digits by a
          fixed formula. UPC-A, EAN-13, GTIN-8/12/13/14, ISBN-13 and SSCC all use the same <strong>GS1 mod-10</strong> algorithm. ISBN-10 uses an older <strong>mod-11 variant</strong> that introduces <code>X</code> as a check character. Code
          128 carries an internal <strong>modulo-103 checksum</strong> that&apos;s invisible in the
          decoded payload — only relevant when you&apos;re generating the symbol itself.
        </p>
        <Callout variant="in-practice" title="One algorithm covers most of what you'll meet">
          <p>
            If you only learn one of these algorithms, learn GS1 mod-10. It covers every retail GTIN
            length, every modern ISBN, every SSCC pallet identifier and every GS1-128 traceability
            barcode. The other algorithms are footnotes by comparison.
          </p>
        </Callout>
      </GuideArticleSection>

      <GuideArticleSection id="mod-10" title="GS1 Mod-10: The Math Walkthrough">
        <p>
          Take the worked example that runs through this site&apos;s guides: GTIN-13 <code>5012345678900</code>. The first twelve digits are the body; the last digit is the
          check.
        </p>
        <ol>
          <li>
            <strong>Take the body.</strong> Strip the trailing check digit, leaving <code>501234567890</code>.
          </li>
          <li>
            <strong>Read right-to-left, alternating weights of 3 and 1, starting with 3.</strong> For <code>501234567890</code>:
            <pre>
              <code>{`Body (reversed): 0  9  8  7  6  5  4  3  2  1  0  5
Weights:         3  1  3  1  3  1  3  1  3  1  3  1
Products:        0  9 24  7 18  5 12  3  6  1  0  5
Sum:             0+9+24+7+18+5+12+3+6+1+0+5 = 90`}</code>
            </pre>
          </li>
          <li>
            <strong>The check digit is whatever brings the sum to the next multiple of 10.</strong> 90 is already a multiple of 10, so the check digit is <code>0</code>. Append to the body
            and you have the full GTIN-13: <code>5012345678900</code>.
          </li>
        </ol>
        <Callout variant="spec" title="The same algorithm for every GTIN length">
          <p>
            UPC-A (GTIN-12), EAN-8 (GTIN-8), GTIN-14 (the carton form) and SSCC (18 digits, AI 00)
            all use the identical right-to-left ×3/×1 weighting. Only the body length changes.
            ISBN-13 (978/979 prefix) is just a GTIN-13 with a fixed prefix, so the algorithm and
            answer are identical.
          </p>
        </Callout>
        <p>
          The calculator runs this same algorithm for any GTIN body — useful when you&apos;re
          minting new identifiers from a fresh company prefix:
        </p>
        <TryTool
          to="/check-digit-calculator"
          label="Check Digit Calculator"
          description="Compute the GS1 mod-10 check digit for any partial UPC, EAN or GTIN body."
          value="501234567890"
          cta="Compute for 501234567890"
        />
        <p>
          To verify the complete identifier in one step (length class plus check digit) feed it to
          the validator:
        </p>
        <TryTool
          to="/gtin-validator"
          label="GTIN Validator"
          description="Confirm a complete GTIN-8 / 12 / 13 / 14 has the correct mod-10 check digit."
          value="5012345678900"
        />
      </GuideArticleSection>

      <GuideArticleSection id="why-it-catches" title="Why Mod-10 Catches Typos and Transpositions">
        <p>
          Mod-10 isn&apos;t a cryptographic hash — it&apos;s a deliberately cheap algorithm that
          catches the most common human errors with one digit of address-space tax. Two properties
          make it useful:
        </p>
        <ul>
          <li>
            <strong>Every single-digit error is caught.</strong> Change any one digit of the body by
            1, the sum changes by either 1 or 3, and the resulting check digit moves with it.
            Mismatch → flagged.
          </li>
          <li>
            <strong>Most adjacent transpositions are caught.</strong> Swap two neighbouring digits
            and the sum changes by 2×(difference between them). Whenever that difference is anything
            other than 0 or 5, the check digit moves and the typo is caught. The blind spot —
            swapping a <code>0</code> and a <code>5</code>, or a <code>1</code> and a <code>6</code> — accounts for about 11% of transpositions.
          </li>
        </ul>
        <Callout variant="heads-up" title="What mod-10 doesn't catch">
          <p>
            It doesn&apos;t catch double transpositions, deliberate fraud or two errors that happen
            to cancel out. It&apos;s a hygiene check, not a security check. If you&apos;re
            validating identifiers as part of a fraud-prevention pipeline, mod-10 is the first
            filter, not the last one.
          </p>
        </Callout>
        <p>
          In practice, mod-10 catches the entire class of single-digit typos and the vast majority
          of transpositions — the two errors people make most often when re-keying numbers. The
          math is often credited to Hans Peter Luhn at IBM, but his formula is a different mod-10
          scheme (compared below); the 3-1 weighting has been the GS1 default since GS1 existed.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="isbn-10" title="ISBN-10's Mod-11 Variant">
        <p>
          ISBN-10 — the old ten-digit book identifier — uses a different algorithm. Same spirit (a
          trailing check digit), different math.
        </p>
        <ol>
          <li>Take the body — the first nine digits of the ISBN-10.</li>
          <li>
            Read <em>left-to-right</em> and multiply each digit by a descending weight starting at
            10:
            <pre>
              <code>{`Body (left-to-right): 0  1  4  3  0  3  3  4  4
Weights:              10 9  8  7  6  5  4  3  2
Products:              0  9 32 21  0 15 12 12  8
Sum:                   0+9+32+21+0+15+12+12+8 = 109`}</code>
            </pre>
          </li>
          <li>
            The check digit is whatever brings the sum to the next multiple of 11. 109 → next
            multiple of 11 is 110, so the check digit is <code>1</code>. Full ISBN-10: <code>0143033441</code>.
          </li>
        </ol>
        <Callout variant="spec" title="Where the X comes from">
          <p>
            Mod-11 can produce a check value of 10, which doesn&apos;t fit in a single decimal
            digit. The publishing convention is to write the Roman numeral <code>X</code> instead.
            You&apos;ll only ever see <code>X</code> as the final character of an ISBN-10, never
            anywhere else. ISBN-13 (introduced in 2007) uses GS1 mod-10, which has no X-as-10
            edge case.
          </p>
        </Callout>
        <p>
          Most book systems today carry ISBN-13 alongside the legacy ISBN-10; the ISBN reader on
          this site validates both and surfaces whichever your input started from.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="code-128" title="Code 128's modulo-103 checksum">
        <p>
          Code 128 (and its GS1-128 cousin) carries an internal checksum that uses <strong>modulo-103</strong> — the symbology has 103 data values (0 to 102), so the sum is taken modulo 103, and each value is
          weighted by its position (1, 2, 3 and so on). The checksum is appended as an extra codeword inside the bar
          pattern; it&apos;s never part of the decoded payload.
        </p>
        <Callout variant="in-practice" title="You don't see it">
          <p>
            Scanning a Code 128 returns the payload string. The modulo-103 check is verified
            silently by the scanner&apos;s firmware. If the check fails, the scanner returns a read
            error rather than a corrupted payload — the entire point of the checksum is to make that
            failure clean.
          </p>
        </Callout>
        <p>
          For most readers and developers, the practical takeaway is short: Code 128 has its own
          check digit, you don&apos;t compute it, you don&apos;t see it, and it&apos;s why a
          scratched Code 128 either reads correctly or doesn&apos;t read at all. For the spec-level
          walkthrough of subsets and FNC1, see the <Link to="/barcode-guides/code-128-explained">Code 128 explained guide</Link>.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="common-mistakes" title="Common Mistakes">
        <p>
          Most check digit failures in real data come from how numbers are handled, not from the
          formula. Five habits cause most of them:
        </p>
        <ul>
          <li>
            <strong>Recomputing the check digit and overwriting the supplier&apos;s value.</strong> If the supplied check doesn&apos;t match, the right action is to flag the row for human
            review — not to silently rewrite the digit. The original input might be the source of
            truth.
          </li>
          <li>
            <strong>Using the wrong algorithm.</strong> ISBN-10 input fed into a GS1 mod-10
            validator returns garbage; an ISBN-13 fed into a mod-11 validator does the same. Route
            inputs to the correct algorithm based on length and prefix.
          </li>
          <li>
            <strong>Storing the check digit separately from the body.</strong> Some legacy systems
            split GTIN into &quot;body&quot; and &quot;check&quot; columns. That makes it too easy
            to re-concatenate them in the wrong order. Store the complete identifier as a single
            string.
          </li>
          <li>
            <strong>Skipping validation on internal SKUs.</strong> If your internal codes happen to
            be 12 or 13 digits, they&apos;ll look like GTINs but won&apos;t validate. Either give
            them an obviously non-GTIN format, or add a check digit of your own.
          </li>
          <li>
            <strong>Assuming a valid check digit means the GTIN exists.</strong> Mod-10 checks the
            math; it doesn&apos;t confirm the identifier was issued to a real company by GS1. For
            ownership verification you need GS1 Verified-by-GS1, not a local check.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="what-is-a-check-digit" title="What Is a Check Digit?">
        <p>
          A check digit is a redundant digit added to an identification number so that a computer
          can tell whether the number was keyed or scanned correctly. It carries no information of
          its own: its value is fixed by the other digits.
        </p>
        <p>
          A check digit detects errors but never corrects them. A failed check says that something in
          the number is wrong, not which digit, so a system should reject the input or ask for it
          again rather than guess.
        </p>
        <p>
          In GS1 identification keys the check digit always takes the last position, and it is part
          of the key: the GTIN-13 <code>5012345678900</code> is thirteen digits long including its
          final <code>0</code>. A scanner verifies it on every read of an EAN or UPC symbol, and
          software should verify it again whenever a GTIN is typed, imported or copied from a
          document. The <Link to="/gtin-validator">GTIN Validator</Link> runs that test on a
          complete 8-, 12-, 13- or 14-digit GTIN.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="luhn-vs-gs1" title="Luhn vs GS1 Mod 10">
        <p>
          Luhn and GS1 mod 10 are both modulus-10 check digit formulas, but they weight the digits
          differently. GS1 multiplies the digits alternately by 3 and 1, while Luhn doubles every
          second digit and adds up the digits of each product.
        </p>
        <p>
          The Luhn algorithm is the check on payment card numbers (ISO/IEC 7812-1) and on the IMEI
          numbers that identify mobile phones. GS1 mod 10 is the check on every GTIN, SSCC and GLN.
          The table sets the two side by side.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Comparison of the GS1 mod 10 and Luhn check digit formulas: where each is used, the
              weights, how products are summed and which transpositions slip through.
            </caption>
            <thead>
              <tr>
                <th scope="col">Aspect</th>
                <th scope="col">GS1 mod 10</th>
                <th scope="col">Luhn</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Used on</th>
                <td>GTIN, SSCC, GLN and other GS1 keys</td>
                <td>Payment card numbers, IMEI</td>
              </tr>
              <tr>
                <th scope="row">Weights, from the right of the body</th>
                <td>3, 1, 3, 1 …</td>
                <td>2, 1, 2, 1 …</td>
              </tr>
              <tr>
                <th scope="row">Products of 10 or more</th>
                <td>Added as they are</td>
                <td>Digits added together (16 counts as 7)</td>
              </tr>
              <tr>
                <th scope="row">Check digit</th>
                <td>Lifts the total to a multiple of 10</td>
                <td>Lifts the total to a multiple of 10</td>
              </tr>
              <tr>
                <th scope="row">Adjacent swaps missed</th>
                <td>Digits that differ by 5, such as 1 and 6</td>
                <td>Only 0 and 9</td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
        <p>
          The two formulas give different answers for the same body. Luhn applied to <code>501234567890</code> returns a check digit of <code>8</code>, not the <code>0</code> that GS1 mod 10 gives, so a Luhn routine would reject many correct EANs and pass some wrong ones. Use a GS1 routine, such as the one in the <Link to="/check-digit-calculator">Check Digit Calculator</Link>, for anything printed in an EAN, UPC or GS1-128 barcode.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="check-digit-examples" title="Check Digit Examples">
        <p>
          The examples below show one worked GS1 check digit per identifier length, so you can
          compare your own results. In every case the check digit formula is the same: weight the
          body 3, 1, 3, 1 from the right, add the products, and take whatever lifts the sum to the
          next multiple of 10.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Worked GS1 mod-10 check digits for EAN-8, UPC-A, EAN-13, ISBN-13, GTIN-14 and SSCC:
              body, weighted sum, check digit and full number.
            </caption>
            <thead>
              <tr>
                <th scope="col">Identifier</th>
                <th scope="col">Body</th>
                <th scope="col">Weighted sum</th>
                <th scope="col">Check digit</th>
                <th scope="col">Full number</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">EAN-8 (GTIN-8)</th>
                <td><code>7351353</code></td>
                <td>63</td>
                <td>7</td>
                <td><code>73513537</code></td>
              </tr>
              <tr>
                <th scope="row">UPC-A (GTIN-12)</th>
                <td><code>03600029145</code></td>
                <td>58</td>
                <td>2</td>
                <td><code>036000291452</code></td>
              </tr>
              <tr>
                <th scope="row">EAN-13 (GTIN-13)</th>
                <td><code>501234567890</code></td>
                <td>90</td>
                <td>0</td>
                <td><code>5012345678900</code></td>
              </tr>
              <tr>
                <th scope="row">ISBN-13</th>
                <td><code>978014303344</code></td>
                <td>82</td>
                <td>8</td>
                <td><code>9780143033448</code></td>
              </tr>
              <tr>
                <th scope="row">GTIN-14</th>
                <td><code>1061414100041</code></td>
                <td>55</td>
                <td>5</td>
                <td><code>10614141000415</code></td>
              </tr>
              <tr>
                <th scope="row">SSCC</th>
                <td><code>10614141123456789</code></td>
                <td>143</td>
                <td>7</td>
                <td><code>106141411234567897</code></td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
        <p>
          The ISBN-13 row is the same book as the ISBN-10 <code>0143033441</code> worked above: drop
          the old check digit, put 978 in front and recalculate with mod 10. The two check digits
          differ, 1 under mod 11 and 8 under mod 10, because each comes from its own formula.
        </p>
        <h3>Check Digit for a UPC Code</h3>
        <p>
          To find the check digit for a UPC code, take the first 11 digits of the UPC-A and count
          positions from the left. For <code>03600029145</code>, the digits in odd positions (0, 6,
          0, 2, 1, 5) add up to 14, and three times 14 is 42. The digits in even positions (3, 0, 0,
          9, 4) add up to 16.
        </p>
        <p>
          The total is 42 + 16 = 58, and the next multiple of 10 is 60, so the check digit is 2 and
          the full UPC-A is <code>036000291452</code>. This is how a check digit is calculated for
          any 11-digit UPC body, and the <Link to="/check-digit-calculator">Check Digit Calculator</Link> gives the same answer when you paste the body in.
        </p>
        <p>
          A UPC-E has no formula of its own. Its check digit is the one calculated for the 12-digit
          UPC-A it expands to, so expand the UPC-E first and check that UPC-A.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "Every UPC, EAN, GTIN, ISBN-13 and SSCC uses the same GS1 mod-10 check digit — right-to-left ×3/×1 weighting, sum to next multiple of 10.",
          "ISBN-10 is the odd one out: mod-11 with descending weights, and X as the check character for value 10.",
          "Mod-10 catches every single-digit error and about 89% of adjacent transpositions — the two most common re-keying mistakes.",
          "Code 128's modulo-103 checksum lives inside the symbol; it's verified silently and never appears in the decoded payload.",
          "If a supplied check digit doesn't match, flag the row for review — don't silently rewrite it.",
        ]}
      />

      <GuideArticleSection id="references" title="References">
        <p>The three algorithms in this guide are defined in these standards.</p>
        <ul>
          <li>
            GS1 General Specifications — defines the mod-10 algorithm for every GS1 identifier.
            Published by <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              GS1
            </a> and updated annually.
          </li>
          <li>
            ISO 2108 (ISBN) — the international standard that defines ISBN-10 (mod-11) and ISBN-13
            (GS1 mod-10). Available from the <a href="https://www.iso.org/standards.html" rel="noopener noreferrer" target="_blank">
              ISO catalogue
            </a>
            .
          </li>
          <li>
            ISO/IEC 15417 (Code 128) — defines the modulo-103 checksum and the symbol&apos;s
            internal structure: <a href="https://www.iso.org/standards.html" rel="noopener noreferrer" target="_blank">
              iso.org/standards
            </a>
            .
          </li>
        </ul>
      </GuideArticleSection>

      <p>
        The fast path: paste a body into the <Link to="/check-digit-calculator">Check Digit Calculator</Link> to compute the correct
        trailing digit, or a full identifier into the <Link to="/gtin-validator">GTIN Validator</Link> to confirm the printed one matches.
      </p>

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
